import { ImagePlus, Link2, Loader2, Upload, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { useAdminUpload } from '../lib/api/uploads';
import { resizeImageIfNeeded } from '../lib/imageResize';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { Input } from './ui/input';

interface ImageUploadFieldProps {
  /** Current image URL (from the form's RHF value). Empty string when unset. */
  value: string;
  /** Called whenever the URL changes — paste, clear, or upload-success. */
  onChange: (url: string) => void;
  /**
   * Bucket label persisted to `admin_uploads.category`. Lets future admin
   * surfaces filter by use-case ("all programme covers"). Defaults to
   * `'general'` if omitted.
   */
  category?: string;
  /** Optional helper text shown below the field. */
  hint?: string;
  /** Disabled state — passed through to inputs and the drop-zone clicker. */
  disabled?: boolean;
  /** Width of the preview thumbnail. Default 96px (suits sidebar layouts). */
  previewSize?: number;
  /**
   * Largest width-or-height (whichever is bigger) the upload may have. Files
   * larger than this get downscaled in the browser before upload — saves
   * bandwidth (a 4 MB phone photo becomes ~200 KB at 1024px) and avoids
   * shipping pixels nobody renders. Files already smaller skip the resize
   * step entirely. Set 0 to disable the client-side resize altogether.
   * Default 2048px is roughly retina-display-friendly for any normal
   * editorial use. Tuning suggestions: 1024 for thumbnail-sized covers,
   * 1920 for hero images.
   */
  maxDimension?: number;
}

/**
 * Editor-friendly image upload widget. Two modes share one URL value:
 *
 *   - **Upload**: drop a file or click the dropzone → multipart POST to
 *     `/admin/uploads` → returns an absolute URL → set as the form value.
 *   - **Paste URL**: text input below the dropzone for the legacy
 *     "paste a URL" flow (still works for external CDN images).
 *
 * Whichever path is used, `onChange(url)` fires with the resolved URL.
 * The form / RHF doesn't need to know which mode produced the value.
 *
 * Preview thumbnail renders whenever `value` is set; the X button next to
 * the URL input clears the value back to empty string.
 */
export function ImageUploadField({
  value,
  onChange,
  category = 'general',
  hint,
  disabled = false,
  previewSize = 96,
  maxDimension = 2048,
}: ImageUploadFieldProps): JSX.Element {
  const upload = useAdminUpload(category);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);
  // Distinct loading state for the resize step — typically <500ms but for
  // 8 MP+ phone photos the canvas pass takes long enough that without a
  // visible cue the editor wonders if the click registered.
  const [resizing, setResizing] = useState(false);

  async function handleFile(file: File): Promise<void> {
    setUploadError(null);
    setImageError(false);
    try {
      // maxDimension=0 opt-out → skip the client-side resize entirely. Any
      // value > 0 runs the helper, which itself short-circuits when the
      // file is small enough to skip work.
      let prepared = file;
      if (maxDimension > 0) {
        setResizing(true);
        try {
          prepared = await resizeImageIfNeeded(file, { maxDimension });
        } finally {
          setResizing(false);
        }
      }
      const result = await upload.mutateAsync(prepared);
      onChange(result.url);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    }
  }

  function onSelect(e: React.ChangeEvent<HTMLInputElement>): void {
    const f = e.target.files?.[0];
    if (f) void handleFile(f);
    // Reset so picking the same file twice still fires onChange.
    e.target.value = '';
  }

  function onDrop(e: React.DragEvent<HTMLLabelElement>): void {
    e.preventDefault();
    setDragOver(false);
    if (disabled || upload.isPending || resizing) return;
    const f = e.dataTransfer.files?.[0];
    if (f) void handleFile(f);
  }

  return (
    <div className="space-y-2">
      <div className="flex items-start gap-3">
        {/* Preview thumbnail OR placeholder slot. The `group` class on this
         *  container scopes the hover-reveal of the X button below — without
         *  it, the button stays at opacity-0 forever. */}
        <div
          className="group relative shrink-0 overflow-hidden rounded-md border border-line bg-surface-tinted"
          style={{ width: previewSize, height: previewSize }}
        >
          {value && !imageError ? (
            <img
              src={value}
              alt=""
              onError={() => setImageError(true)}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="grid h-full w-full place-items-center text-ink-subtle">
              <ImagePlus className="h-5 w-5" />
            </div>
          )}
          {value ? (
            <button
              type="button"
              onClick={() => onChange('')}
              disabled={disabled || upload.isPending || resizing}
              aria-label="Remove image"
              className="absolute right-1 top-1 grid h-5 w-5 place-items-center rounded-full bg-ink/70 text-white opacity-0 transition-opacity hover:bg-ink group-hover:opacity-100 focus:opacity-100"
            >
              <X className="h-3 w-3" />
            </button>
          ) : null}
        </div>

        {/* Drop zone + click-to-pick. The label-wrapped hidden file input
         *  gives us the native file picker keyboard + screen-reader behavior
         *  without any of the custom-button gymnastics. */}
        <label
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled && !upload.isPending && !resizing) setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={cn(
            'flex min-h-[96px] flex-1 cursor-pointer flex-col items-center justify-center gap-1.5 rounded-md border-2 border-dashed px-3 py-3 text-center text-[12.5px] leading-snug transition-colors',
            dragOver
              ? 'border-brand bg-brand-soft/40 text-brand'
              : 'border-line text-ink-muted hover:border-line-strong hover:bg-surface-tinted',
            (disabled || upload.isPending || resizing) && 'pointer-events-none opacity-50',
          )}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={onSelect}
            disabled={disabled || upload.isPending || resizing}
            className="sr-only"
          />
          {resizing || upload.isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="font-medium">
                {resizing ? 'Preparing image…' : 'Uploading…'}
              </span>
            </>
          ) : (
            <>
              <Upload className="h-4 w-4" />
              <span className="font-medium">
                Drop an image or <span className="text-brand">click to upload</span>
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-ink-subtle">
                JPG · PNG · WebP · GIF · max 10 MB
              </span>
            </>
          )}
        </label>
      </div>

      {/* Paste-URL fallback. Same value, alternate input mode. */}
      <div className="flex items-center gap-2">
        <Link2 className="h-3.5 w-3.5 shrink-0 text-ink-subtle" />
        <Input
          type="url"
          inputMode="url"
          placeholder="…or paste an image URL"
          value={value}
          onChange={(e) => {
            // Clear the prior load-failure flag so the warning text
            // disappears as soon as the user starts editing the URL.
            if (imageError) setImageError(false);
            onChange(e.target.value);
          }}
          disabled={disabled || upload.isPending || resizing}
          className="flex-1 text-[13px]"
        />
        {value ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange('')}
            disabled={disabled || upload.isPending || resizing}
            aria-label="Clear URL"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        ) : null}
      </div>

      {uploadError ? (
        <p className="text-xs text-danger">{uploadError}</p>
      ) : imageError && value ? (
        <p className="text-xs text-danger">Couldn't load that image — check the URL.</p>
      ) : hint ? (
        <p className="text-xs text-ink-subtle">{hint}</p>
      ) : null}
    </div>
  );
}
